import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import api from '../api/api';
import Alert from '../components/Alert';
import Icon from '../components/Icon';
import './notifications.css';

// What each email is, in one line. Ids match the backend's email types.
const EMAIL_TYPES = [
    { id: 'weeklyPlan', label: 'Weekly plan', description: 'Monday morning: your week\'s posts, or a nudge to plan them if the week is empty.' },
    { id: 'postingReminders', label: 'Posting reminders', description: 'Mornings with planned posts not yet posted, with links to write them.' },
    { id: 'weeklyDigest', label: 'Weekly results', description: 'Last week\'s views, likes and clicks against targets, your top post and what to try next.' },
    { id: 'usageAlerts', label: 'Usage alerts', description: 'When you\'ve used 80% of this month\'s generations, and when they run out.' },
    { id: 'productUpdates', label: 'Product tips & updates', description: 'New features and tips for getting more from light speed. A few a month at most.' },
];

const HOURS = Array.from({ length: 24 }, (_, hour) => hour);
const formatHour = (hour) => `${hour % 12 || 12}:00 ${hour < 12 ? 'AM' : 'PM'}`;

const browserTimezone = () => {
    try {
        return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
    } catch (e) {
        return 'UTC';
    }
};

// "Europe/London · GMT+1"
const timezoneLabel = (zone) => {
    let offset = '';
    try {
        offset = new Intl.DateTimeFormat('en-US', { timeZone: zone, timeZoneName: 'shortOffset' })
            .formatToParts(new Date()).find(part => part.type === 'timeZoneName')?.value || '';
    } catch (e) {
        offset = '';
    }
    return `${zone.replace(/_/g, ' ')}${offset ? ` · ${offset}` : ''}`;
};

const listTimezones = (extra) => {
    let zones = [];
    try {
        zones = Intl.supportedValuesOf('timeZone');
    } catch (e) {
        zones = [];
    }
    return [...new Set([...zones, 'UTC', ...extra.filter(Boolean)])].sort();
};

const SAVE_DELAY_MS = 500;

// Merges a change into the not-yet-saved changes (email toggles merge one level deep)
const mergeChanges = (base, change) => ({
    ...base,
    ...change,
    ...((base.emails || change.emails) && { emails: { ...base.emails, ...change.emails } }),
});

const errorMessage = (error, fallback) => error?.response?.data?.error || fallback;

// A checkbox that looks and announces itself as a switch
const Switch = ({ checked, onChange, labelledBy, describedBy }) => (
    <span className="ls-notify-switch">
        <input
            type="checkbox"
            role="switch"
            checked={checked}
            onChange={event => onChange(event.target.checked)}
            aria-labelledby={labelledBy}
            aria-describedby={describedBy}
        />
        <span className="ls-notify-switch-track" aria-hidden="true">
            <span className="ls-notify-switch-thumb" />
        </span>
    </span>
);

const ToggleRow = ({ id, label, description, checked, muted, onChange }) => (
    <label className={`ls-notify-row${muted ? ' is-muted' : ''}`}>
        <span className="ls-notify-text">
            <span id={`${id}-label`} className="ls-notify-title">{label}</span>
            <span id={`${id}-desc`} className="ls-notify-desc">{description}</span>
        </span>
        <Switch checked={checked} onChange={onChange} labelledBy={`${id}-label`} describedBy={`${id}-desc`} />
    </label>
);

const SaveStatus = ({ status, onRetry }) => {
    if (status === 'saving') {
        return <span className="ls-notify-status" role="status"><span className="ls-spinner" />Saving…</span>;
    }
    if (status === 'saved') {
        return <span className="ls-notify-status is-saved" role="status"><Icon name="check" size={16} />Saved</span>;
    }
    if (status === 'error') {
        return (
            <span className="ls-notify-status is-error" role="alert">
                <Icon name="error" size={16} />Couldn't save.
                <button type="button" className="ls-link" onClick={onRetry}>Try again</button>
            </span>
        );
    }
    return <span className="ls-notify-status">Changes save automatically</span>;
};

const RowSkeleton = ({ titleWidth, descWidth }) => (
    <div className="ls-notify-row is-locked">
        <span className="ls-notify-text">
            <span className="ls-notify-title"><span className="ls-skeleton-text" style={{ width: titleWidth }} /></span>
            <span className="ls-notify-desc"><span className="ls-skeleton-text" style={{ width: descWidth }} /></span>
        </span>
        <div className="ls-skeleton" style={{ width: 40, height: 24, borderRadius: 999, flexShrink: 0 }} />
    </div>
);

const FieldSkeleton = () => (
    <div className="ls-field">
        <span className="ls-label"><span className="ls-skeleton-text" style={{ width: 70 }} /></span>
        <div className="ls-skeleton" style={{ height: 45, borderRadius: 12 }} />
    </div>
);

const LoadingCards = () => (
    <div className="ls-two-col" aria-busy="true" aria-label="Loading email settings">
        <div className="ls-card">
            <div className="ls-card-header"><span className="ls-skeleton-text" style={{ width: 140, height: 14 }} /></div>
            <div className="ls-notify-list">
                {[['30%', '85%'], ['36%', '75%'], ['32%', '90%'], ['28%', '70%'], ['44%', '80%'], ['20%', '78%']].map(([title, desc]) => (
                    <RowSkeleton key={title + desc} titleWidth={title} descWidth={desc} />
                ))}
            </div>
        </div>
        <div className="ls-col">
            <div className="ls-card">
                <span className="ls-skeleton-text" style={{ width: 120, height: 14 }} />
                <div className="ls-form-grid"><FieldSkeleton /><FieldSkeleton /></div>
                <div className="ls-notify-list"><RowSkeleton titleWidth="40%" descWidth="90%" /></div>
            </div>
            <div className="ls-card">
                <span className="ls-skeleton-text" style={{ width: 130, height: 14 }} />
                <div className="ls-skeleton" style={{ height: 45, borderRadius: 12 }} />
            </div>
        </div>
    </div>
);

const NotificationsScreen = () => {
    const [settings, setSettings] = useState(null);
    const [email, setEmail] = useState('');
    const [loadError, setLoadError] = useState('');
    const [saveStatus, setSaveStatus] = useState('idle'); // idle | saving | saved | error
    const [testKind, setTestKind] = useState(EMAIL_TYPES[0].id);
    const [testState, setTestState] = useState({ busy: false, sentTo: '', error: '' });

    // Autosave: changes collect here and go out together after a short pause;
    // one request at a time, the next waits for the one in flight
    const pending = useRef({});
    const timer = useRef(null);
    const inFlight = useRef(false);
    const mounted = useRef(true);

    useEffect(() => {
        mounted.current = true;
        return () => {
            mounted.current = false;
            clearTimeout(timer.current);
        };
    }, []);

    const flush = useCallback(async () => {
        clearTimeout(timer.current);
        if (inFlight.current || Object.keys(pending.current).length === 0) return;
        const changes = pending.current;
        pending.current = {};
        inFlight.current = true;
        if (mounted.current) setSaveStatus('saving');
        try {
            await api.updateNotificationSettings(changes);
            inFlight.current = false;
            if (!mounted.current) return;
            if (Object.keys(pending.current).length) {
                flush();
            } else {
                setSaveStatus('saved');
            }
        } catch (error) {
            inFlight.current = false;
            // Keep the choices on screen and retry with everything not yet saved
            pending.current = mergeChanges(changes, pending.current);
            if (mounted.current) setSaveStatus('error');
        }
    }, []);

    const update = useCallback((change) => {
        setSettings(current => mergeChanges(current, change));
        pending.current = mergeChanges(pending.current, change);
        setSaveStatus('saving');
        clearTimeout(timer.current);
        timer.current = setTimeout(flush, SAVE_DELAY_MS);
    }, [flush]);

    const load = useCallback(async () => {
        setLoadError('');
        setSettings(null);
        try {
            const data = await api.getNotificationSettings();
            if (!mounted.current) return;
            setEmail(data.email || '');
            const loaded = data.settings;
            // First visit: use this browser's time zone, and save it quietly
            if (!loaded.timezoneSaved) {
                const zone = browserTimezone();
                loaded.timezone = zone;
                api.updateNotificationSettings({ timezone: zone }).catch(() => {});
            }
            setSettings(loaded);
        } catch (error) {
            if (mounted.current) setLoadError(errorMessage(error, 'We couldn\'t load your email settings.'));
        }
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    const localZone = useMemo(browserTimezone, []);
    const knownZones = useMemo(() => listTimezones([localZone]).map(zone => ({ zone, label: timezoneLabel(zone) })), [localZone]);
    // A saved zone this browser doesn't list (older or newer tz data) still shows
    const timezones = settings && !knownZones.some(({ zone }) => zone === settings.timezone)
        ? [...knownZones, { zone: settings.timezone, label: timezoneLabel(settings.timezone) }]
        : knownZones;

    const sendTest = async () => {
        setTestState({ busy: true, sentTo: '', error: '' });
        try {
            const result = await api.sendTestEmail(testKind);
            setTestState({ busy: false, sentTo: result.to || email, error: '' });
        } catch (error) {
            setTestState({ busy: false, sentTo: '', error: errorMessage(error, 'We couldn\'t send the test email. Please try again.') });
        }
    };

    const paused = Boolean(settings?.paused);

    return (
        <>
            <div className="ls-page-header">
                <div>
                    <div className="ls-eyebrow">Account</div>
                    <h1 className="ls-page-title">Email updates</h1>
                </div>
                {settings && <SaveStatus status={saveStatus} onRetry={flush} />}
            </div>

            {loadError && (
                <Alert
                    severity="error"
                    action={<button type="button" className="ls-btn ls-btn-secondary" onClick={load}>Try again</button>}
                >
                    {loadError}
                </Alert>
            )}

            {!settings && !loadError && <LoadingCards />}

            {settings && (
                <div className="ls-two-col">
                    <section className="ls-card" aria-labelledby="notify-emails-title">
                        <div className="ls-card-header">
                            <div>
                                <h2 id="notify-emails-title" className="ls-card-title ls-notify-heading">What we email you</h2>
                                <div className="ls-card-sub">
                                    {paused ? 'Paused: none of these are sent until you resume.' : `Sent to ${email || 'your account email'}`}
                                </div>
                            </div>
                        </div>
                        <div className="ls-notify-list">
                            {EMAIL_TYPES.map(type => (
                                <ToggleRow
                                    key={type.id}
                                    id={`notify-${type.id}`}
                                    label={type.label}
                                    description={type.description}
                                    checked={Boolean(settings.emails?.[type.id])}
                                    muted={paused}
                                    onChange={on => update({ emails: { [type.id]: on } })}
                                />
                            ))}
                            <div className="ls-notify-row is-locked">
                                <span className="ls-notify-text">
                                    <span className="ls-notify-title">Billing</span>
                                    <span className="ls-notify-desc">
                                        Trial ending and failed payments. Always sent, so a card problem never stops your plan.
                                    </span>
                                </span>
                                <span className="ls-chip is-medium ls-notify-locked">
                                    <Icon name="lock" size={14} />Always on
                                </span>
                            </div>
                        </div>
                    </section>

                    <div className="ls-col">
                        <section className="ls-card" aria-labelledby="notify-when-title">
                            <h2 id="notify-when-title" className="ls-card-title ls-notify-heading">When</h2>
                            <div className="ls-form-grid">
                                <div className="ls-field">
                                    <label className="ls-label" htmlFor="notify-hour">Send at</label>
                                    <select
                                        id="notify-hour"
                                        className="ls-select"
                                        value={settings.sendHour}
                                        onChange={event => update({ sendHour: Number(event.target.value) })}
                                    >
                                        {HOURS.map(hour => <option key={hour} value={hour}>{formatHour(hour)}</option>)}
                                    </select>
                                </div>
                                <div className="ls-field">
                                    <label className="ls-label" htmlFor="notify-zone">Time zone</label>
                                    <select
                                        id="notify-zone"
                                        className="ls-select"
                                        value={settings.timezone}
                                        onChange={event => update({ timezone: event.target.value })}
                                    >
                                        {timezones.map(({ zone, label }) => <option key={zone} value={zone}>{label}</option>)}
                                    </select>
                                </div>
                            </div>
                            <div className="ls-hint ls-notify-hint">
                                {settings.timezone !== localZone ? (
                                    <>
                                        This browser is on {localZone.replace(/_/g, ' ')}.{' '}
                                        <button type="button" className="ls-link ls-notify-hint-link" onClick={() => update({ timezone: localZone })}>
                                            Use it
                                        </button>
                                    </>
                                ) : 'Morning emails arrive around this time, your local time.'}
                            </div>
                            <div className="ls-notify-list">
                                <ToggleRow
                                    id="notify-paused"
                                    label="Pause all emails"
                                    description="Stops every email above until you turn this off. Billing emails still come through."
                                    checked={paused}
                                    onChange={on => update({ paused: on })}
                                />
                            </div>
                        </section>

                        <section className="ls-card" aria-labelledby="notify-test-title">
                            <div>
                                <h2 id="notify-test-title" className="ls-card-title ls-notify-heading">Send a test</h2>
                                <div className="ls-card-sub">See what an email looks like, with your own plan when there's one.</div>
                            </div>
                            <div className="ls-notify-test">
                                <select
                                    aria-label="Email to test"
                                    className="ls-select"
                                    value={testKind}
                                    onChange={event => {
                                        setTestKind(event.target.value);
                                        setTestState(state => ({ ...state, sentTo: '', error: '' }));
                                    }}
                                >
                                    {EMAIL_TYPES.map(type => <option key={type.id} value={type.id}>{type.label}</option>)}
                                </select>
                                <button type="button" className="ls-btn ls-btn-secondary" onClick={sendTest} disabled={testState.busy}>
                                    {testState.busy ? <span className="ls-spinner" /> : <Icon name="send" size={18} />}
                                    Send me a test email
                                </button>
                            </div>
                            {testState.error ? (
                                <Alert severity="error" onClose={() => setTestState(state => ({ ...state, error: '' }))}>{testState.error}</Alert>
                            ) : (
                                <div className={`ls-notify-test-result${testState.sentTo ? ' is-sent' : ''}`} role="status">
                                    {testState.sentTo ? `Sent to ${testState.sentTo}. It can take a minute to arrive.` : ''}
                                </div>
                            )}
                        </section>
                    </div>
                </div>
            )}
        </>
    );
};

export default NotificationsScreen;
